import {
  Box,
  Container,
  Divider,
  Flex,
  Link,
  PrimaryButton,
  Text,
  UnderlineButton,
  VStack,
} from "flicket-ui";
import { useRouter } from "next/router";
import { useContext, useMemo, useState } from "react";
import {
  CalendarBlank,
  IconContext,
  MapPin,
  Wheelchair,
} from "@phosphor-icons/react";
import { EventsCalendar } from "~components/calendar/calendar";
import {
  ContentModal,
  Footer,
  Icon,
  Menu,
  PageContainer,
  Seo,
} from "~components";
import { Layout } from "~components/common/Layout";
import { SlateRenderer } from "~components/common/Slate/SlateRenderer";
import StickySection from "~components/common/StickySection";
import { UserContext } from "~context";
import { useEventsService } from "~features/events/hooks/useEventsService";
import { CalendarHomepageCustomizationDocument } from "~graphql/typed-document-nodes";
import { useQuery } from "~hooks/useQuery";
import {
  getCalendarAccessibilityRichText,
  getCalendarLocationMapsUrl,
} from "~lib/helpers/calendarHomepage";
import { formatEventDateRange } from "~lib/helpers/formatDate";

const CALENDAR_VIEW_ID = "calendar-view";
const CALENDAR_MONTH_CONTROLS_ID = "calendar-month-controls";
const CALENDAR_DESKTOP_MEDIA_QUERY = "(min-width: 768px)";
const CALENDAR_SCROLL_GAP_MOBILE = 12;
const CALENDAR_SCROLL_GAP_DESKTOP = 40;
const CALENDAR_SCROLL_RECHECK_DELAY_MS = 450;

const CalendarHomePage = () => {
  const router = useRouter();
  const { isAdmin } = useContext(UserContext);
  const [isAccessibilityModalOpen, setAccessibilityModalOpen] = useState(false);

  const { data } = useQuery(CalendarHomepageCustomizationDocument);
  const { events: allEvents } = useEventsService({
    includeFeaturedEvents: true,
    hiddenFromPublic: false,
  });

  const customization = data?.calendarHomepageCustomization;
  const pageTitle = customization?.title || "Events calendar";
  const calendarLocation = customization?.location?.trim() || null;
  const calendarLocationMapsUrl = getCalendarLocationMapsUrl(calendarLocation, {
    latitude: customization?.locationLatitude,
    longitude: customization?.locationLongitude,
  });
  const calendarAccessibility = customization?.accessibility || null;
  const calendarAccessibilityRichText = useMemo(
    () => getCalendarAccessibilityRichText(calendarAccessibility),
    [calendarAccessibility]
  );
  const activeCalendarEvents = useMemo(
    () =>
      (allEvents || []).filter((event) => isAdmin || !event.hiddenFromPublic),
    [allEvents, isAdmin]
  );
  const hasAccessibility = !!calendarAccessibilityRichText;

  const activeDateRange = useMemo(() => {
    if (!activeCalendarEvents.length) return null;

    const firstEvent = activeCalendarEvents.reduce((earliest, current) => {
      return new Date(current.startDate).getTime() <
        new Date(earliest.startDate).getTime()
        ? current
        : earliest;
    }, activeCalendarEvents[0]);

    const lastEvent = activeCalendarEvents.reduce((latest, current) => {
      const latestEndDate = latest.endDate || latest.startDate;
      const currentEndDate = current.endDate || current.startDate;

      return new Date(currentEndDate).getTime() >
        new Date(latestEndDate).getTime()
        ? current
        : latest;
    }, activeCalendarEvents[0]);

    return formatEventDateRange(
      firstEvent.startDate,
      lastEvent.endDate || lastEvent.startDate,
      {
        format: "detailedShort",
        showStartTime: false,
        showEndTime: false,
        timeZone: firstEvent.venue?.timezone,
        locale: firstEvent.venue?.locale,
      }
    );
  }, [activeCalendarEvents]);

  function handleSelectEvent(eventId: string) {
    void router.push({
      pathname: `/events/[eventId]/reservation`,
      query: {
        ...router.query,
        eventId: eventId,
      },
    });
  }

  function scrollToCalendarView() {
    const getBuyNowHeaderHeight = () => {
      const buyNowHeaders = document.querySelectorAll(
        '[data-calendar-buy-now-header="true"]'
      );

      return Array.from(buyNowHeaders).reduce(
        (height, header) =>
          header instanceof HTMLElement
            ? Math.max(height, header.getBoundingClientRect().height)
            : height,
        0
      );
    };

    const getScrollGap = () => {
      const isDesktop = window.matchMedia(CALENDAR_DESKTOP_MEDIA_QUERY).matches;
      return isDesktop
        ? CALENDAR_SCROLL_GAP_DESKTOP
        : CALENDAR_SCROLL_GAP_MOBILE;
    };

    const monthControls = document.getElementById(CALENDAR_MONTH_CONTROLS_ID);
    const calendarViewElement = document.getElementById(CALENDAR_VIEW_ID);
    const targetElement = monthControls ?? calendarViewElement;
    if (!targetElement) return;

    const scrollGap = getScrollGap();
    const buyNowHeaderHeight = getBuyNowHeaderHeight();

    const targetPosition =
      window.scrollY + targetElement.getBoundingClientRect().top;

    window.scrollTo({
      top: Math.max(targetPosition - buyNowHeaderHeight - scrollGap, 0),
      behavior: "smooth",
    });

    window.setTimeout(() => {
      const latestMonthControls = document.getElementById(
        CALENDAR_MONTH_CONTROLS_ID
      );
      if (!(latestMonthControls instanceof HTMLElement)) return;

      const latestScrollGap = getScrollGap();
      const latestHeaderHeight = getBuyNowHeaderHeight();
      const minVisibleTop = latestHeaderHeight + latestScrollGap;
      const monthControlsTop = latestMonthControls.getBoundingClientRect().top;

      if (monthControlsTop < minVisibleTop) {
        window.scrollBy({
          top: monthControlsTop - minVisibleTop,
          behavior: "auto",
        });
      }
    }, CALENDAR_SCROLL_RECHECK_DELAY_MS);
  }

  const calendarMetaInformation = (
    <IconContext.Provider
      value={{
        size: 24,
        weight: "light",
      }}
    >
      <VStack alignItems="flex-start" gap={1} mt="1/2">
        {activeDateRange && (
          <Flex alignItems="center">
            <Icon icon={<CalendarBlank />} color="N800" />
            <Text variant="regular" ml={1}>
              {activeDateRange}
            </Text>
          </Flex>
        )}

        {calendarLocation && (
          <Link
            display="flex"
            target="_blank"
            href={calendarLocationMapsUrl ?? undefined}
          >
            <Icon icon={<MapPin />} color="N800" />
            <Text variant="regular" ml={1}>
              {calendarLocation}
            </Text>
          </Link>
        )}

        {hasAccessibility && (
          <Flex alignItems="center">
            <Icon icon={<Wheelchair />} color="N800" />
            <UnderlineButton
              onClick={() => setAccessibilityModalOpen(true)}
              color="N800"
            >
              <Text variant="regular" ml={1}>
                Accessibility info
              </Text>
            </UnderlineButton>
          </Flex>
        )}
      </VStack>
    </IconContext.Provider>
  );

  return (
    <>
      <Seo title={pageTitle} />
      {hasAccessibility && (
        <ContentModal
          isOpen={isAccessibilityModalOpen}
          setIsOpen={() => setAccessibilityModalOpen(false)}
          title="Accessibility"
          richText={calendarAccessibilityRichText ?? ""}
        />
      )}

      <Layout
        layoutType="main"
        css={`
          padding-top: 0 !important;
        `}
      >
        <Menu sticky={false} />

        <PageContainer
          bannerImage={customization?.banner || null}
          footer={<Footer />}
          containerProps={{
            pt: 3,
            minHeight: { _: 0, md: "320px" },
          }}
        >
          <VStack gap="1/2">
            <StickySection offset={0}>
              {({ isSticky }) => (
                <Box
                  data-calendar-buy-now-header="true"
                  bg="white"
                  py={isSticky ? 0 : 2}
                  css={`
                    border-bottom: 1px solid #ececf0;
                  `}
                  boxShadow={isSticky ? "sm" : undefined}
                >
                  {isSticky ? (
                    <Container
                      innerProps={{
                        // @ts-expect-error override box shadow
                        boxShadow: "none !important",
                      }}
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems={{ _: "stretch", md: "center" }}
                        flexDirection={{ _: "column", md: "row" }}
                        gap={2}
                      >
                        <Box>
                          <Text variant="header.L" as="h1" mb={1}>
                            {pageTitle}
                          </Text>
                          {calendarMetaInformation}
                        </Box>

                        <PrimaryButton
                          onClick={scrollToCalendarView}
                          mt={{ _: 1, md: 0 }}
                          width={{ _: "100%", md: "auto" }}
                        >
                          Buy now
                        </PrimaryButton>
                      </Flex>
                    </Container>
                  ) : (
                    <Flex
                      justifyContent="space-between"
                      alignItems={{ _: "stretch", md: "center" }}
                      flexDirection={{ _: "column", md: "row" }}
                      gap={2}
                    >
                      <Box>
                        <Text variant="header.L" as="h1" mb={1}>
                          {pageTitle}
                        </Text>
                        {calendarMetaInformation}
                      </Box>

                      <PrimaryButton
                        onClick={scrollToCalendarView}
                        mt={{ _: 1, md: 0 }}
                        width={{ _: "100%", md: "auto" }}
                      >
                        Buy now
                      </PrimaryButton>
                    </Flex>
                  )}
                </Box>
              )}
            </StickySection>

            {customization?.description && (
              <SlateRenderer value={customization.description} mt={3} />
            )}

            <Divider mt={3} />
          </VStack>

          <Box mt={4} id={CALENDAR_VIEW_ID}>
            <EventsCalendar handleSelectEvent={handleSelectEvent} />
          </Box>
        </PageContainer>
      </Layout>
    </>
  );
};

export default CalendarHomePage;
